import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../../services/apiClient";

// Fetch all mortgages
export const fetchMortgages = createAsyncThunk(
  'mortgage/fetchAll',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/mortgages?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Fetch single mortgage
export const fetchMortgage = createAsyncThunk(
  'mortgage/fetchOne',
  async ({ canvasId, mortgageId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/mortgages/${mortgageId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Create Mortgage
export const createMortgage = createAsyncThunk(
  'mortgage/create',
  async ({ canvasId, mortgageData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/canvas/${canvasId}/mortgages`, mortgageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Update Mortgage
export const updateMortgage = createAsyncThunk(
  'mortgage/update',
  async ({ canvasId, mortgageId, mortgageData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/canvas/${canvasId}/mortgages/${mortgageId}`, mortgageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Delete Mortgage
export const deleteMortgage = createAsyncThunk(
  'mortgage/delete',
  async ({ canvasId, mortgageId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/canvas/${canvasId}/mortgages/${mortgageId}`);
      return mortgageId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload Debts from file
export const uploadMortgages = createAsyncThunk(
  'mortgage/upload',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/canvas/${canvasId}/mortgages/upload`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const mortgageOverview = createAsyncThunk(
  'mortgage/overview',
  async ({canvasId}, {rejectWithValue})=>{
    try{
      const response = await apiClient.get(`/canvas/${canvasId}/mortgages/overview`);
      return response.data
    }catch(err){
      return rejectWithValue(err)
    }
  }
)

export const fetchMortgagePayments = createAsyncThunk(
  'mortgage/fetchMortgagePayments',
  async ({ canvasId, mortgageId }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/canvas/${canvasId}/mortgages/${mortgageId}/repayments`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const addMortgagePayment = createAsyncThunk(
  'mortgage/addMortgagePayment',
  async ({ canvasId, mortgageId, amount, date }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/canvas/${canvasId}/mortgages/${mortgageId}/repayments`, { amount, date });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const editMortgagePayment = createAsyncThunk(
  'mortgage/editMortgagePayment',
  async ({ canvasId, paymentId, amount, date }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/canvas/${canvasId}/mortgages/repayments/${paymentId}`, { amount, date });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteMortgagePayment = createAsyncThunk(
  'mortgage/deleteMortgagePayment',
  async ({ canvasId, paymentId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/canvas/${canvasId}/mortgages/repayments/${paymentId}`);
      return paymentId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Mortgage Slice
const mortgageSlice = createSlice({
  name: "mortgage",
  initialState: {
    mortgages: [],
    mortgage: null,
    loading: false,
    error: null,
    pagination: null,
    overview:null,
    mortgagePayments: [],
    mortgagePaymentsLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all mortgages
      .addCase(fetchMortgages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMortgages.fulfilled, (state, action) => {
        state.loading = false;
        state.mortgages = action.payload.mortgages || action.payload.items || [];
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchMortgages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Fetch single mortgage
      .addCase(fetchMortgage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMortgage.fulfilled, (state, action) => {
        state.loading = false;
        state.mortgage = action.payload;
      })
      .addCase(fetchMortgage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Create mortgage
      .addCase(createMortgage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMortgage.fulfilled, (state, action) => {
        state.loading = false;
        state.mortgages = [action.payload, ...state.mortgages];
      })
      .addCase(createMortgage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Update mortgage
      .addCase(updateMortgage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMortgage.fulfilled, (state, action) => {
        state.loading = false;
        state.mortgages = state.mortgages.map((m) =>
          m._id === action.payload._id ? action.payload : m
        );
      })
      .addCase(updateMortgage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Delete mortgage
      .addCase(deleteMortgage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMortgage.fulfilled, (state, action) => {
        state.loading = false;
        state.mortgages = state.mortgages.filter((m) => m._id !== action.payload);
      })
      .addCase(deleteMortgage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      //upload Mortgages
      .addCase(uploadMortgages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadMortgages.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadMortgages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      .addCase(mortgageOverview.fulfilled, (state, action)=>{
        state.loading =false;
        state.overview = action.payload;
      })
      // Mortgage Payments
      .addCase(fetchMortgagePayments.pending, (state) => {
        state.mortgagePaymentsLoading = true;
      })
      .addCase(fetchMortgagePayments.fulfilled, (state, action) => {
        state.mortgagePaymentsLoading = false;
        state.mortgagePayments = action.payload;
      })
      .addCase(fetchMortgagePayments.rejected, (state) => {
        state.mortgagePaymentsLoading = false;
        state.mortgagePayments = [];
      })
      .addCase(addMortgagePayment.fulfilled, (state, action) => {
        state.mortgagePayments.unshift(action.payload);
      })
      .addCase(editMortgagePayment.fulfilled, (state, action) => {
        state.mortgagePayments = state.mortgagePayments.map(p =>
          p._id === action.payload._id ? action.payload : p
        );
      })
      .addCase(deleteMortgagePayment.fulfilled, (state, action) => {
        state.mortgagePayments = state.mortgagePayments.filter(p => p._id !== action.payload);
      })
  },
});

export const selectMortgages = state => state.mortgage.mortgages;
export const selectMortgageLoading = state => state.mortgage.loading;
export const selectMortgagePagination = state => state.mortgage.pagination;
export const selectMortgageOverview = state=> state.mortgage.overview;
export const selectMortgagePayments = state => state.mortgage.mortgagePayments;
export const selectMortgagePaymentsLoading = state => state.mortgage.mortgagePaymentsLoading;
export default mortgageSlice.reducer;